<template>
  <div class="space-y-6">

    <!-- Notice on Checkout Success -->
    <alert v-if="$route.query.success" type="success" icon="check-circle">
      Thank you! Your payment details were saved successfully.
    </alert>

    <!-- Notice on Checkout Cancel -->
    <alert v-if="$route.query.cancel" type="warning" icon="times-circle">
      Session cancelled. Your card details were not saved. You may try again, if you like.
    </alert>
    
    <!-- <template v-if="account.subscription.trialing">
      <div class="p-12 card">
        <div>
          <div class="flex items-center justify-center w-12 h-12 mx-auto bg-indigo-100 rounded-full">
            <fa :icon="['far', 'clock']" class="text-xl text-indigo-600" />
          </div>
          <div class="mt-3 text-center sm:mt-5">
            <h3 class="text-xl font-bold leading-6 text-gray-900">
              <template v-if="account.subscription.trial_time_remaining.value > 0">
                {{ account.subscription.trial_time_remaining.value }} {{ account.subscription.trial_time_remaining.unit }} Left On Your Free <Trial></Trial>
              </template>
              <template v-else>
                Your Free Trial Has Ended
              </template>
            </h3>
            <div class="mt-2">
              <p class="max-w-md mx-auto text-gray-500">
                Subscribe to a plan and enter your payment details to continue using Fleetbeacon.
              </p>
              <router-link :to="{ name: 'SettingsSubscription' }" class="mt-6 btn btn-lg btn-primary">
                Subscribe to a Plan &rarr;
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </template> -->

    <div class="card">
      <div class="card-header">
        Payment Method
      </div>
      <div class="p-6">
        <alert class="mb-6" type="info" icon="info-circle">
          If you'd like to change your preferred payment method please send an email to <a href="mailto:billing@fleetbeacon.app">billing@fleetbeacon.app</a> with your request.
        </alert>

        <div v-if="account.billing_method === 'eft'">
          <div class="font-bold">Bank Transfer (EFT)</div>
          <div>You've elected to pay via bank transfer (EFT), you will receive monthly invoices from Fleetbeacon. Please pay using the following details:</div>
          <div class="mt-5">
            <strong class="inline-block w-64">Bank:</strong> FNB <br>
            <strong class="inline-block w-64">Account #:</strong> 62742862651  <br>
            <strong class="inline-block w-64">Branch Code:</strong> 250655 <br>
            <strong class="inline-block w-64">Business Reg. #:</strong> 2017 / 652816 / 07 <br>
            <strong class="inline-block w-64">SWIFT Code:</strong> FIRNZAJJ  <br>
            <strong class="inline-block w-64">Webfleet Partner Number:</strong> 133554 <br>
            <strong class="inline-block w-64">Reference:</strong> The Invoice Number
          </div>
        </div>

        <div v-else-if="account.billing_method === 'webfleet'">
          <div class="font-bold">Webfleet Contract</div>
          <div>You will be invoiced by Webfleet as part of your contract.</div>
        </div>

        <div v-else-if="account.billing_method === 'card'">
          <div class="font-bold">
            Credit Card
          </div>
          <div class="text-gray-500">
            Your card will be debited by our PCI-compliant transaction providers, Stripe (stripe.com).
          </div>

          <div v-if="loading" class="mt-4">
            <Loading />
          </div>
          <div v-else-if="paymentMethods.length < 1" class="mt-4"> 
            <alert type="default" icon="credit-card">
              No cards have been added yet.
            </alert>
          </div>
          <div v-else class="grid gap-4 mt-4">
            <div v-for="method in paymentMethods" :key="method.id" class="flex items-center gap-4 p-4 border rounded hover:bg-gray-50">
              <div>
                <fa :icon="['fab', `cc-${method.card.brand}`]" class="text-3xl text-gray-500" />
              </div>
              <div class="text-sm leading-none">
                <div>
                  &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; {{ method.card.last4 }}
                </div>
                <div class="text-gray-500">
                  Exp. {{ method.card.exp_month }} / {{ method.card.exp_year }}
                </div>
              </div>
              <button 
                class="px-2 py-1 ml-auto text-xs font-medium tracking-wide text-gray-500 uppercase rounded-full bg-gray-50" 
                :class="confirmDelete === method.id ? 'bg-red-500 text-red-50' : 'hover:text-red-500'"
                v-submitting="submitting"
                v-html="confirmDelete === method.id ? 'Really Remove?' : 'Remove'"
                @click="deletePaymentMethod(method.id)">
              </button>
            </div>
          </div>

          <button class="mt-4 btn" :class="paymentMethods.length < 1 ? 'btn-primary' : 'btn-secondary'" v-submitting="submitting" @click="addPaymentMethod">
            Add A Card &rarr;
          </button>
        </div>
      </div>
    </div>

    <div class="card" v-if="account.billing_method === 'card'">
      <div class="card-header">
        Billing Portal
      </div>
      <div class="p-6">
        <div>
          Inside the Billing Portal, you can:
        </div>
        <div class="mt-2 text-gray-500">
          <ul class="list-disc list-inside">
            <li>
              Cancel or update your subscription plan.
            </li>
            <li>
              Add or remove payment methods.
            </li>
            <li>
              Update billing information, like tax numbers.
            </li>
            <li>
              View billing history and past invoices.
            </li>
          </ul>
        </div>

        <button class="mt-4 btn btn-secondary" v-submitting="submitting" @click="openBillingPortal">
          Go to Billing Portal &rarr;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'SettingsBilling',

  metaInfo: {
    title: 'Billing Settings'
  },

  created () {
    this.getPaymentMethods()
  },

  computed: {
    ...mapGetters(['account'])
  },

  data() {
    return {
      confirmDelete: null,
      loading: true,
      paymentMethods: [],
      invoices: [],
      submitting: false
    }
  },

  methods: {
    async getPaymentMethods () {
      this.loading = true
      const { data } = await this.$api.get(`account/${this.account.subdomain}/billing/payment-methods`)
      this.paymentMethods = data.payment_methods
      this.loading = false
    },

    async addPaymentMethod () {
      this.submitting = true
      const { data } = await this.$api.post(`account/${this.account.subdomain}/billing/payment-methods`)
      window.location.href = data.url
      this.submitting = false
    },

    async deletePaymentMethod (id) {
      if (this.confirmDelete !== id) {
        this.confirmDelete = id
        return
      }

      this.submitting = true
      await this.$api.delete(`account/${this.account.subdomain}/billing/payment-methods/${id}`)
      this.submitting = false
      const index = this.paymentMethods.findIndex((pm) => pm.id === id)
      this.$delete(this.paymentMethods, index)
      this.confirmDelete = null
    },

    async openBillingPortal () {
      this.submitting = true
      const { data } = await this.$api.post(`account/${this.account.subdomain}/billing/manage`)
      window.location.href = data.url
      this.submitting = false
    },
  }
}
</script>